import { Outlet, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import PublicNavbar, { PUBLIC_NAV_HEIGHT } from '@/components/public/PublicNavbar';
import { getSectionByKey } from '@/services/siteSections';
import { SalesAdvisorProvider } from '@/components/assistant/SalesAdvisorProvider';
import SalesAdvisor from '@/components/assistant/SalesAdvisor';
import { FakeBookingsSantaRosa } from '../FakeBookingsSantaRosa';
import RuletaPremiosWrapper from '../public/RuletaPremiosWrapper';

export default function PublicDesktopLayout() {
  const [footerData, setFooterData] = useState<any>(null);
  const [logoUrl, setLogoUrl] = useState<string>('');

  useEffect(() => {
    loadFooterData();
    loadLogo();
  }, []);

  const loadLogo = async () => {
    try {
      const heroSection = await getSectionByKey('hero');
      if (heroSection?.content_json?.logo) {
        setLogoUrl(heroSection.content_json.logo);
      }
    } catch (error) {
      console.error('Error loading logo:', error);
    }
  };

  const loadFooterData = async () => {
    try {
      const section = await getSectionByKey('footer');
      if (section) {
        setFooterData(section.content_json);
      }
    } catch (error) {
      console.error('Error loading footer data:', error);
    }
  };

  const shareUrl = window.location.href;
  const shareTitle = 'Fotos y Video Santa Rosa - Capturamos tus momentos especiales';
  const shareText = 'Descubre nuestros servicios de fotografía y video profesional';

  const handleShare = (platform: string) => {
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(shareTitle);
    const encodedText = encodeURIComponent(shareText);

    let url = '';
    switch (platform) {
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case 'twitter':
        url = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`;
        break;
      case 'whatsapp':
        url = `https://wa.me/?text=${encodedText}%20${encodedUrl}`;
        break;
      case 'telegram':
        url = `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`;
        break;
      case 'linkedin':
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
        break;
      case 'email':
        url = `mailto:?subject=${encodedTitle}&body=${encodedText}%20${encodedUrl}`;
        break;
    }

    if (platform === 'email') {
      window.location.href = url;
    } else {
      window.open(url, '_blank', 'width=600,height=400');
    }
  };

  return (
    <SalesAdvisorProvider>
    <div className="min-h-screen flex flex-col bg-white">
      {/* Top Navigation — componente oficial compartido */}
      <PublicNavbar />

      {/* Main Content — reserva la altura real del header fijo (menú + barra de compartir).
          Se usa la constante oficial del navbar, igual que /precios. Sin márgenes negativos. */}
      <main className="flex-1" style={{ paddingTop: PUBLIC_NAV_HEIGHT }}>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-pink-50 to-purple-50 border-t border-pink-100 py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 flex items-center justify-center">
                {logoUrl ? (
                  <img 
                    src={logoUrl} 
                    alt="Fotos y Video Santa Rosa" 
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <i className="ri-camera-lens-fill text-2xl text-pink-600"></i>
                )}
              </div>
              <span className="text-lg font-semibold text-gray-900">Fotos y Video Santa Rosa</span>
            </div>
            
            <div className="flex items-center gap-4">
              {footerData?.whatsapp && (
                <a
                  href={`https://wa.me/${footerData.whatsapp.replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center bg-white rounded-full text-pink-600 hover:bg-pink-600 hover:text-white transition-all shadow-sm cursor-pointer"
                  aria-label="WhatsApp"
                >
                  <i className="ri-whatsapp-line text-xl"></i>
                </a>
              )}
              {footerData?.facebook && (
                <a
                  href={footerData.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center bg-white rounded-full text-pink-600 hover:bg-pink-600 hover:text-white transition-all shadow-sm cursor-pointer"
                  aria-label="Facebook"
                >
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
              )}
              {footerData?.instagram && (
                <a
                  href={footerData.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center bg-white rounded-full text-pink-600 hover:bg-pink-600 hover:text-white transition-all shadow-sm cursor-pointer"
                  aria-label="Instagram"
                >
                  <i className="ri-instagram-line text-xl"></i>
                </a>
              )}
            </div>
          </div>

          {/* Share Buttons in Footer */}
          <div className="mt-6 pt-6 border-t border-pink-100">
            <div className="text-center mb-4">
              <p className="text-sm text-gray-600 mb-3">Comparte esta página con otros</p>
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => handleShare('facebook')}
                  className="w-9 h-9 flex items-center justify-center bg-white/70 hover:bg-blue-600 text-blue-600 hover:text-white rounded-full transition-all shadow-sm cursor-pointer"
                  aria-label="Compartir en Facebook"
                >
                  <i className="ri-facebook-fill text-base"></i>
                </button>
                <button
                  onClick={() => handleShare('twitter')}
                  className="w-9 h-9 flex items-center justify-center bg-white/70 hover:bg-sky-500 text-sky-500 hover:text-white rounded-full transition-all shadow-sm cursor-pointer"
                  aria-label="Compartir en Twitter"
                >
                  <i className="ri-twitter-x-fill text-base"></i>
                </button>
                <button
                  onClick={() => handleShare('whatsapp')}
                  className="w-9 h-9 flex items-center justify-center bg-white/70 hover:bg-green-500 text-green-500 hover:text-white rounded-full transition-all shadow-sm cursor-pointer"
                  aria-label="Compartir en WhatsApp"
                >
                  <i className="ri-whatsapp-fill text-base"></i>
                </button>
                <button
                  onClick={() => handleShare('telegram')}
                  className="w-9 h-9 flex items-center justify-center bg-white/70 hover:bg-cyan-500 text-cyan-500 hover:text-white rounded-full transition-all shadow-sm cursor-pointer"
                  aria-label="Compartir en Telegram"
                >
                  <i className="ri-telegram-fill text-base"></i>
                </button>
                <button
                  onClick={() => handleShare('linkedin')}
                  className="w-9 h-9 flex items-center justify-center bg-white/70 hover:bg-blue-700 text-blue-700 hover:text-white rounded-full transition-all shadow-sm cursor-pointer"
                  aria-label="Compartir en LinkedIn"
                >
                  <i className="ri-linkedin-fill text-base"></i>
                </button>
                <button
                  onClick={() => handleShare('email')}
                  className="w-9 h-9 flex items-center justify-center bg-white/70 hover:bg-gray-600 text-gray-600 hover:text-white rounded-full transition-all shadow-sm cursor-pointer"
                  aria-label="Compartir por Email"
                >
                  <i className="ri-mail-fill text-base"></i>
                </button>
              </div>
            </div>
          </div>
          
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-2">
              {footerData?.copyright || '© 2025 Fotos y Video Santa Rosa. Capturamos tus momentos especiales.'}
            </p>
            <div className="flex items-center justify-center gap-4 mb-2">
              <Link 
                to="/terms.html"
                className="text-xs text-gray-500 hover:text-pink-600 transition-colors"
              >
                Términos y Condiciones
              </Link>
            </div>
            <a 
              href="https://Axloweb.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block text-xs text-gray-500 hover:text-pink-600 transition-colors"
            >
              Designed by Axloweb
            </a>
          </div>
        </div>
      </footer>

      {/* Asesor de ventas IA — instancia única (Alejandro) */}
      <SalesAdvisor />

      {/* Fake Bookings */}
      <FakeBookingsSantaRosa />

      {/* Ruleta de Premios */}
      <RuletaPremiosWrapper />
    </div>
    </SalesAdvisorProvider>
  );
}